import React, { useMemo, useState } from 'react'
import { Box, ClickAwayListener, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useModal, useUser } from 'store'
import ChangelogPanel from '../../Sidebar/components/ChangelogButton/ChangelogPanel'
import { useReleaseNotes } from '../../Sidebar/components/ChangelogButton/use-release-notes'
import { MENU_SUPPORT } from '../../Sidebar/constants'
import { checkUserRoles } from '../utils'

const LANGUAGES = [
  { value: 'fr', label: 'FR' },
  { value: 'en', label: 'EN' },
  { value: 'it', label: 'IT' },
  { value: 'es', label: 'ES' },
  { value: 'de', label: 'DE' }
]

const panelShadow = '0px 1.85px 3.15px 0px #00000014, 0px 8.15px 6.52px 0px #0000000A, 0px 20px 13px 0px #0000000A'

const sidebarTooltipProps = {
  tooltip: {
    sx: {
      backgroundColor: '#292929',
      color: 'white',
      borderRadius: '8px',
      fontSize: 10,
      fontWeight: 500,
      lineHeight: '14px',
      px: 1,
      py: 0.5,
      width: 'max-content',
      maxWidth: 180,
      textAlign: 'center',
      boxShadow: panelShadow
    }
  },
  arrow: {
    sx: {
      color: '#292929'
    }
  }
}

const CircleButton = ({ active, icon, onClick, label }) => {
  const theme = useTheme()

  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        p: 0,
        borderRadius: '50%',
        border: `1px solid ${active ? theme.palette.primary.main : '#EFEFEF'}`,
        backgroundColor: 'white',
        color: '#464646',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 0.15s ease',
        '& i': {
          fontSize: 15
        },
        '&:hover': {
          borderColor: theme.palette.primary.main
        }
      }}
    >
      <i className={icon} />
    </Box>
  )
}

const ArrowPanel = ({ children, width = 224 }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 'calc(100% + 14px)',
      right: 0,
      zIndex: 1300,
      width,
      border: '1px solid #F7F7F7',
      borderRadius: '16px',
      backgroundColor: 'white',
      boxShadow: panelShadow,
      p: 1.5,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -7,
        right: 19,
        width: 12,
        height: 12,
        backgroundColor: 'white',
        borderLeft: '1px solid #F7F7F7',
        borderTop: '1px solid #F7F7F7',
        transform: 'rotate(45deg)'
      }
    }}
  >
    {children}
  </Box>
)

const SettingsItem = ({ color = '#656565', icon, label, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    onClick={onClick}
    sx={{
      height: 34,
      px: 1,
      borderRadius: '8px',
      cursor: 'pointer',
      color,
      '&:hover': {
        backgroundColor: '#F7F7F7'
      }
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 400, color }}>{label}</Typography>
    <i className={icon} style={{ fontSize: 14, color }} />
  </Stack>
)

const isDefaultSupportEntry = entry => entry.url === MENU_SUPPORT.menu[0]?.url

const formatFooterDate = date => {
  if (!date) return '-'
  const parsedDate = new Date(date.replace(' ', 'T'))
  if (Number.isNaN(parsedDate.getTime())) return date
  return new Intl.DateTimeFormat('fr-FR').format(parsedDate)
}

const HeaderActions = () => {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const [user] = useUser()
  const [, setModal] = useModal()
  const [openPanel, setOpenPanel] = useState(null)
  const releaseNotes = useReleaseNotes(i18n.language)
  const latestRelease = releaseNotes.latestRelease
  const canUseDemoMode = user?.admin || user?.roles?.demo_mode

  const supportActions = useMemo(() => {
    if (!user) return []

    return MENU_SUPPORT.menu.filter(entry => {
      if (isDefaultSupportEntry(entry) && ((user?.experience_cloud && user?.roles?.requests) || user?.user_type === 'client')) {
        return true
      }

      return checkUserRoles(user, entry.requiredRoles)
    })
  }, [user])

  const openSupportAction = action => {
    setOpenPanel(null)
    window.open(action.url, '_blank')
  }

  return (
    <ClickAwayListener onClickAway={() => setOpenPanel(null)}>
      <Stack direction="row" gap={1.5} alignItems="center">
        {user?.internal && (
          <Box position="relative">
            <CircleButton
              active={openPanel === 'changelog'}
              icon="fa-light fa-circle-info"
              label={t('changelog.open')}
              onClick={() => setOpenPanel(openPanel === 'changelog' ? null : 'changelog')}
            />
            {openPanel === 'changelog' && (
              <ChangelogPanel
                anchor="topbar"
                hasMoreReleaseNotes={releaseNotes.hasMoreReleaseNotes}
                hasNoReleaseNotes={releaseNotes.hasNoReleaseNotes}
                language={i18n.language}
                onClose={() => setOpenPanel(null)}
                onLoadMore={releaseNotes.loadMore}
                releaseNotes={releaseNotes.visibleReleaseNotes}
              />
            )}
          </Box>
        )}
        {supportActions.length === 1 && (
          <Tooltip
            title={t(supportActions[0].label)}
            placement="top"
            arrow
            componentsProps={sidebarTooltipProps}
          >
            <Box>
              <CircleButton
                active={false}
                icon="fa-light fa-headset"
                label={t(supportActions[0].label)}
                onClick={() => openSupportAction(supportActions[0])}
              />
            </Box>
          </Tooltip>
        )}
        {supportActions.length > 1 && (
          <Box position="relative">
            <CircleButton
              active={openPanel === 'support'}
              icon="fa-light fa-headset"
              label={t('navigation.support.title')}
              onClick={() => setOpenPanel(openPanel === 'support' ? null : 'support')}
            />
            {openPanel === 'support' && (
              <ArrowPanel width={196}>
                <Stack gap={0.25}>
                  {supportActions.map(action => (
                    <SettingsItem
                      key={action.label}
                      icon="fa-light fa-arrow-up-right-from-square"
                      label={t(action.label)}
                      onClick={() => openSupportAction(action)}
                    />
                  ))}
                </Stack>
              </ArrowPanel>
            )}
          </Box>
        )}
        <Box position="relative">
          <CircleButton
            active={openPanel === 'settings'}
            icon="fa-light fa-cog"
            label="Paramètres"
            onClick={() => setOpenPanel(openPanel === 'settings' ? null : 'settings')}
          />
          {openPanel === 'settings' && (
            <ArrowPanel>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                {LANGUAGES.map(language => {
                  const active = i18n.language.split('-')[0] === language.value
                  return (
                    <Box
                      key={language.value}
                      onClick={() => i18n.changeLanguage(language.value)}
                      sx={{
                        width: 32,
                        height: 24,
                        borderRadius: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 10,
                        fontWeight: 500,
                        color: active ? theme.palette.primary.main : '#656565',
                        ...(active && {
                          border: '1px solid #E4EEFA',
                          backgroundColor: '#F2F8FD'
                        }),
                        '&:hover': {
                          backgroundColor: active ? '#F2F8FD' : '#F7F7F7'
                        }
                      }}
                    >
                      {language.label}
                    </Box>
                  )
                })}
              </Stack>
              <Box sx={{ height: '1px', backgroundColor: '#EFEFEF', mx: 1, mb: 1.25 }} />
              <Stack px={1} mb={1.25} alignItems="flex-start">
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#464646', lineHeight: '18px' }}>
                  {[user?.first_name, user?.last_name].filter(Boolean).join(' ')}
                </Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 400, color: '#989898', lineHeight: '14px', textAlign: 'left' }}>
                  {user?.email}
                </Typography>
              </Stack>
              <Stack gap={0.25} mb={1.25}>
                <SettingsItem icon="fa-light fa-user-gear" label={t('navigation.settings.account')} onClick={() => {
                  setOpenPanel(null)
                  navigate('/profile')
                }} />
                {canUseDemoMode && (
                  <SettingsItem icon="fa-light fa-play" label={t('navigation.settings.demo_mode')} onClick={() => {
                    setOpenPanel(null)
                    setModal('demo')
                  }} />
                )}
              </Stack>
              <Box sx={{ height: '1px', backgroundColor: '#EFEFEF', mx: 1, mb: 1.25 }} />
              <SettingsItem
                color="#DC2626"
                icon="fa-light fa-arrow-right-from-bracket"
                label={t('navigation.settings.logout')}
                onClick={() => {
                  setOpenPanel(null)
                  navigate('/auth/logout')
                }}
              />
              <Typography sx={{ mt: 1.25, textAlign: 'center', fontSize: 10, fontWeight: 400, color: '#989898' }}>
                {latestRelease ? `Version ${latestRelease.version}  •  ${formatFooterDate(latestRelease.date)}` : 'Version -  •  -'}
              </Typography>
            </ArrowPanel>
          )}
        </Box>
      </Stack>
    </ClickAwayListener>
  )
}

export default HeaderActions
